import { doNotDisturb } from '@hcengineering/ui'
import { get } from 'svelte/store'
import { type Class, type Doc, type Ref } from '@hcengineering/core'
import { type Asset, getMetadata, getResource } from '@hcengineering/platform'
import { getClient } from '.'
import notification from '@hcengineering/notification'

const sounds = new Map<Asset, AudioBuffer>()
const context = new AudioContext()

function resumeAudio (): void {
  if (context.state !== 'suspended') return
  // A browser can leave resume pending until user interaction. Do not delay
  // playback's stop handle while waiting for autoplay permission.
  void context.resume().catch((err) => { console.error('Cannot resume audio', err) })
}

if (typeof document !== 'undefined') {
  document.addEventListener('pointerdown', resumeAudio, true)
  document.addEventListener('keydown', resumeAudio, true)
}

export async function isNotificationAllowed (_class?: Ref<Class<Doc>>): Promise<boolean> {
  if (_class === undefined) return false
  const client = getClient()
  const notificationType = client
    .getModel()
    .findAllSync(notification.class.NotificationType, { objectClass: _class })[0]

  if (notificationType === undefined) return false

  const isAllowedFn = await getResource(notification.function.IsNotificationAllowed)
  return isAllowedFn(notificationType, notification.providers.SoundNotificationProvider)
}

export async function prepareSound (key: string): Promise<void> {
  try {
    const soundUrl = getMetadata(key as Asset) as string
    const rawAudio = await fetch(soundUrl)
    const rawBuffer = await rawAudio.arrayBuffer()
    const decodedBuffer = await context.decodeAudioData(rawBuffer)

    sounds.set(key as Asset, decodedBuffer)
  } catch (err) {
    console.error('Sound not found', key)
  }
}

export async function playSound (soundKey: string, loop = false): Promise<(() => void) | null> {
  if (get(doNotDisturb)) return null
  resumeAudio()
  const soundAssetKey = soundKey as Asset

  if (!sounds.has(soundAssetKey)) {
    await prepareSound(soundKey)
  }

  if (get(doNotDisturb)) return null
  const sound = sounds.get(soundKey as Asset)
  if (sound === undefined) {
    console.error('Cannot prepare audio buffer', soundKey)
    return null
  }

  try {
    const audio = context.createBufferSource()
    audio.buffer = sound
    audio.loop = loop
    audio.connect(context.destination)
    audio.start()

    let stopped = false
    let unsubscribe = (): void => {}
    const stop = (): void => {
      if (stopped) return
      stopped = true
      audio.stop()
      audio.disconnect(context.destination)
      unsubscribe()
    }
    unsubscribe = doNotDisturb.subscribe((enabled) => { if (enabled) stop() })
    audio.onended = () => { unsubscribe() }
    return stop
  } catch (err) {
    console.error('Error when playing sound back', soundKey, err)
    return null
  }
}

export async function playNotificationSound (
  soundKey: string,
  _class?: Ref<Class<Doc>>,
  loop = false
): Promise<(() => void) | null> {
  const allowed = await isNotificationAllowed(_class)
  if (!allowed) return null
  return await playSound(soundKey, loop)
}
